/**
 * The page every Steward document is printed on.
 *
 * Extracted from the plat sheet, which is where it grew up: a bordered Letter
 * page with the subject's name knocked out of a black bar at the top, the ward
 * and the date and "3 of 18" knocked out of another at the bottom. The map
 * sheets earned that furniture; the incident packet gets it too, so a walker
 * holding one page of either can tell what it is and where it came from.
 *
 * What lives here is anything that would have to be identical between two
 * documents for them to look like one system — the paper, the margins, the
 * bars, the footer stamp — plus the small marks a form is made of: a box you
 * tick with a pen, a rule you write on, a heading with a line under it.
 *
 * What does NOT live here is anything that knows what is being drawn. The map
 * belongs to plat/sheet.ts and the packet to incident/packet.ts.
 */
import { PT_PER_IN, textWidth, type PdfDoc, type PdfPage } from './pdf';

export const LETTER_SHORT = 8.5 * PT_PER_IN;
export const LETTER_LONG = 11 * PT_PER_IN;

/** Distance from the paper's edge to the printed border. */
export const MARGIN = 0.4 * PT_PER_IN;
/** Distance from the border to anything inside it. */
export const PAD = 9;

export const TITLE_PT = 12;
export const SUB_PT = 8.5;
export const FOOT_PT = 7.5;

/**
 * The running head and foot are solid bars with the type knocked out white.
 *
 * They occupy exactly the space two hairline rules would divide off, so the
 * content is drawn in the same box either way — this is a change of colour,
 * not of layout.
 */
const BAR_GRAY = 0;
/** The bar's own subject: the sector or section, and the ward on the foot. */
export const BAR_TEXT = 1;
/** Everything else in a bar — subordinate, but still legible on black. */
export const BAR_TEXT_MUTED = 0.72;
/**
 * Helvetica's cap height as a fraction of point size.
 *
 * What "centred in the bar" means for a line of type is that the capitals
 * straddle the middle, not that the baseline sits on it — a baseline centred
 * literally leaves the text looking high, because everything but the
 * descenders is above it.
 */
export const CAP_HEIGHT = 0.717;

export type Box = { x0: number; y0: number; x1: number; y1: number };

/**
 * The two bars, in page coordinates.
 *
 * Shared, because `furniture` fills them when the page is made and
 * `stampFooters` writes into the foot once the page count is known; two
 * copies of these numbers would drift.
 */
export function bars(w: number, h: number) {
  void w;
  const top = h - MARGIN - PAD - TITLE_PT;
  return {
    head: { y0: top - 5, y1: h - MARGIN },
    foot: { y0: MARGIN, y1: MARGIN + PAD + FOOT_PT + 4 },
  };
}

/** Baseline that sits `size`pt type in the middle of a bar. */
export const baselineIn = (bar: { y0: number; y1: number }, size: number) =>
  (bar.y0 + bar.y1) / 2 - (size * CAP_HEIGHT) / 2;

export const pageSize = (landscape: boolean) => ({
  w: landscape ? LETTER_LONG : LETTER_SHORT,
  h: landscape ? LETTER_SHORT : LETTER_LONG,
});

/** What is left inside the border once the running head and foot have theirs. */
export function contentBox(w: number, h: number): Box {
  return {
    x0: MARGIN + PAD,
    x1: w - MARGIN - PAD,
    y0: MARGIN + PAD + FOOT_PT + 6,
    y1: h - MARGIN - PAD - TITLE_PT - 6,
  };
}

/**
 * The page border and the two running bars.
 *
 * The subject heads the page and the ward's name runs along the bottom edge,
 * so a sheet that has been carried around in a car and handed to somebody else
 * still says what it is and whose it is.
 */
export function furniture(page: PdfPage, title: string, right: string,
                          flag?: string | null) {
  const w = page.w, h = page.h;
  page.dash(0, 0);

  // The bars first, then the border over them, so the border stays a crisp
  // line rather than being half-swallowed by the fill it abuts.
  page.fillGray(BAR_GRAY);
  const { head, foot } = bars(w, h);
  page.rect(MARGIN, head.y0, w - MARGIN * 2, head.y1 - head.y0, 'f');
  page.rect(MARGIN, foot.y0, w - MARGIN * 2, foot.y1 - foot.y0, 'f');
  page.fillGray(0);

  page.strokeGray(0);
  page.lineWidth(1);
  page.rect(MARGIN, MARGIN, w - MARGIN * 2, h - MARGIN * 2, 'S');

  // Each centred on its own size rather than sharing a baseline: they sit at
  // opposite ends of a wide bar, where being level with each other is not
  // something the eye can check but being off-centre in the bar is.
  page.text(title, MARGIN + PAD, baselineIn(head, TITLE_PT),
            { size: TITLE_PT, bold: true, gray: BAR_TEXT });

  // A flag rides at the right end of the bar, in full white with the page's
  // own subtitle pushed left of it. It is for the one thing that must be true
  // on every page whether or not anybody reads the rest — that this is a
  // drill. In the bar rather than under it: a second black bar for one word
  // doubles the furniture and halves what it means.
  let x = w - MARGIN - PAD;
  if (flag) {
    const tracking = 1.2;
    page.text(flag, x, baselineIn(head, SUB_PT),
              { size: SUB_PT, bold: true, align: 'right', gray: BAR_TEXT, tracking });
    x -= textWidth(flag, SUB_PT, true) + tracking * (flag.length - 1) + 12;
  }
  if (right) {
    page.text(right, x, baselineIn(head, SUB_PT),
              { size: SUB_PT, align: 'right', gray: BAR_TEXT_MUTED });
  }
}

/**
 * The footer, stamped once the document is finished.
 *
 * It says "3 of 18", which nothing knows until the last page exists. The bar
 * itself is laid down by `furniture` when the page is made; this only knocks
 * the type out of it.
 */
export function stampFooters(doc: PdfDoc, left: string, centre: string) {
  const pages = doc.all;
  pages.forEach((page, i) => {
    const y = baselineIn(bars(page.w, page.h).foot, FOOT_PT);
    page.text(left, MARGIN + PAD, y, { size: FOOT_PT, gray: BAR_TEXT });
    page.text(centre, page.w / 2, y,
              { size: FOOT_PT, align: 'center', gray: BAR_TEXT_MUTED });
    page.text(`${i + 1} of ${pages.length}`, page.w - MARGIN - PAD, y,
              { size: FOOT_PT, align: 'right', gray: BAR_TEXT_MUTED });
  });
}

/** Trim a string to fit a column, with an ellipsis where it was cut. */
export function clip(s: string, maxW: number, size: number, bold = false): string {
  if (textWidth(s, size, bold) <= maxW) return s;
  let out = s;
  while (out.length > 1 && textWidth(`${out}…`, size, bold) > maxW) {
    out = out.slice(0, -1);
  }
  return `${out.trimEnd()}…`;
}

// ── The marks a form is made of ────────────────────────────────────────────
//
// Drawn, never typed. The packet used to be HTML printed by the browser,
// where a checkbox had to be a bordered <span> because print stylesheets hide
// form controls; here a box is four lines on paper and nothing can hide it.

/** A box to tick with a pen. `ticked` draws the X the app already knows about. */
export function checkbox(page: PdfPage, x: number, y: number, size = 8,
                         ticked = false) {
  page.dash(0, 0);
  page.strokeGray(0);
  page.lineWidth(0.7);
  page.rect(x, y, size, size, 'S');
  if (ticked) {
    page.lineWidth(1.1);
    page.segments([
      [{ x: x + 1.5, y: y + 1.5 }, { x: x + size - 1.5, y: y + size - 1.5 }],
      [{ x: x + 1.5, y: y + size - 1.5 }, { x: x + size - 1.5, y: y + 1.5 }],
    ]);
  }
}

/** A rule to write on. The answer the system does not have. */
export function writeRule(page: PdfPage, x: number, y: number, w: number) {
  page.dash(0, 0);
  page.strokeGray(0.45);
  page.lineWidth(0.5);
  page.segments([[{ x, y }, { x: x + w, y }]]);
}

/**
 * A section heading with a rule under it, returning the baseline to carry on
 * from. Sized to sit clearly below body copy without becoming a second title —
 * the bar at the top of the page is the title, and only one thing can be.
 */
export function heading(page: PdfPage, text: string, x: number, y: number,
                        w: number, size = 8.5): number {
  page.text(text.toUpperCase(), x, y, { size, bold: true, tracking: 0.6 });
  page.dash(0, 0);
  page.strokeGray(0);
  page.lineWidth(0.8);
  page.segments([[{ x, y: y - 3.5 }, { x: x + w, y: y - 3.5 }]]);
  return y - 3.5 - size * 1.5;
}

/**
 * A labelled line: the label in grey, then either the value or a rule to
 * write it on.
 *
 * Every fact on the info page is drawn this way, because a page that prints
 * only what it knows is a page with holes in it, and a page of blanks is a
 * form. This is both: the same line answered where we can answer it and
 * writable where we cannot.
 */
export function labelled(page: PdfPage, label: string, value: string | null,
                         x: number, y: number, w: number,
                         labelW = 96, size = 8.5) {
  // Clipped, not allowed to overrun: a long label that runs under its own
  // write-in rule is worse than an abbreviated one, because the rule is the
  // part somebody has to write on.
  page.text(clip(label, labelW - 8, size), x, y, { size, gray: 0.4 });
  const vx = x + labelW;
  const vw = w - labelW;
  if (value) {
    page.text(clip(value, vw, size, true), vx, y, { size, bold: true });
  } else {
    writeRule(page, vx, y - 2, vw);
  }
}

/**
 * Ruled lines to write on, filling a given height.
 *
 * Every page in the packet ends up with slack somewhere, and slack on an
 * emergency document should be writable. What people actually record here —
 * the road that is out, the shelter that opened — is the information the
 * system did not have when it printed.
 */
export function notesArea(page: PdfPage, x: number, y: number, w: number,
                          h: number, lead = 16) {
  page.dash(0, 0);
  page.strokeGray(0.55);
  page.lineWidth(0.4);
  for (let ly = y; ly > y - h; ly -= lead) {
    page.segments([[{ x, y: ly }, { x: x + w, y: ly }]]);
  }
}
